<template>
	<div ref="el" data-component="body-video">
		<div v-if="isYouTubeBlock && youtubeVideoId" class="youtube-container">
			<ScriptYouTubePlayer ref="youtubePlayerRef" :video-id="youtubeVideoId" :player-vars="youtubePlayerVars" @state-change="stateChange">
				<template #default>
					<button v-if="!hasStarted" ref="youtubeCoverRef" type="button" class="video-cover" aria-label="Play video" @click="handleYouTubeCoverClick">
						<div class="video-grad" />
						<div class="video-play">
							<Icon size="xxl" slug="ic-play" class="play-icon" />
						</div>
						<div class="video-tag">
							<Icon size="sm" slug="ic-camera" />
							<p class="-label-regular tag-label">Video</p>
						</div>
						<h3 v-if="coverTitle" class="-headline-5 video-title">{{ coverTitle }}</h3>
					</button>
				</template>
			</ScriptYouTubePlayer>
		</div>

		<div v-else-if="isCdnVideoBlock && videoAsset" ref="videoPlayer" class="video-container" @mouseenter="handleMouseEnter" @mouseleave="handleMouseLeave">
			<VideoAsset :asset="videoAsset" :controls="isTouch && hasStarted" />
			<button v-if="!hasStarted" ref="cdnCoverRef" type="button" class="video-cover" aria-label="Play video" @click="handleCdnCoverClick">
				<div class="video-grad" />
				<div class="video-play">
					<Icon size="xxl" slug="ic-play" class="play-icon" />
				</div>
				<div class="video-tag">
					<Icon size="sm" slug="ic-camera" />
					<p class="-label-regular tag-label">Video</p>
				</div>
				<h3 v-if="coverTitle" class="-headline-5 video-title">{{ coverTitle }}</h3>
			</button>
			<div v-if="!isTouch && hasStarted" ref="controlsRef" class="controls-container">
				<div class="controls">
					<div class="row">
						<Icon size="md" slug="ic-mute" :class="`mute-icon ${isMuted ? 'active' : ''}`" @click="handleMute()" />
						<div @click="handlePlayStop()">
							<Icon v-if="isPlaying" size="md" slug="ic-pause" class="play-icon" />
							<Icon v-else size="md" slug="ic-play" class="play-icon" />
						</div>
						<Icon size="md" slug="ic-expand" class="maximize-icon" @click="handleMaximize()" />
					</div>
					<div class="row">
						<span class="time -label-regular">{{ cursorTime }}</span>
						<div ref="timeBar" class="time-bar-container" @click="handleTimeBarClick">
							<div class="time-bar" />
							<div class="current-time-bar" :style="{ width: `${cursorPosition}%` }" />
							<div class="cursor" :style="{ left: `${cursorPosition}%` }" />
						</div>
						<span class="time -label-regular"> {{ totalTime }}</span>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { gsap } from 'gsap'
import { useScreen } from 'vue-screen'
import { VideoAsset, Icon } from '~/components/core'

const props = defineProps({
	value: { type: Object, default: undefined },
	url: { type: String, default: undefined },
	video: { type: Object, default: undefined },
})

const el = ref(null)
const context = inject('pageContext')
const controlsRef = ref(null)
const videoPlayer = ref(null)
const youtubePlayerRef = ref(null)
const cdnCoverRef = ref(null)
const youtubeCoverRef = ref(null)
const screen = useScreen()
const isTouch = ref(false)
const isPlaying = ref(false)
const isMuted = ref(true)
const currentTime = ref(0)
const displayTime = ref(0)
const duration = ref(0)
const timeBar = ref(null)
const showControls = ref(true)
const hasStarted = ref(false)
const isCoverFading = ref(false)

const isYouTubeBlock = computed(() => props.value?._type === 'youtubeEmbed')
const isCdnVideoBlock = computed(() => props.value?._type === 'videoAsset')
const videoAsset = computed(() => props.video || props.value?.video || null)
const coverTitle = computed(() => {
	return 'Learn how Silkriver works through this tutorial'

	if (typeof videoAsset.value?.caption !== 'string') return ''
	return videoAsset.value.caption.trim()
})
const youtubePlayerVars = {
	controls: 1,
	playsinline: 1,
	modestbranding: 1,
	rel: 0,
	iv_load_policy: 3,
}

const extractVideoID = (urlValue) => {
	if (!urlValue) return ''

	const normalizedUrl = String(urlValue).trim()
	if (!normalizedUrl) return ''

	try {
		const parsed = new URL(normalizedUrl)
		const hostname = parsed.hostname.replace(/^www\./, '')

		if (hostname === 'youtu.be') {
			return parsed.pathname.split('/').filter(Boolean).at(-1) || ''
		}

		const queryParamId = parsed.searchParams.get('v')
		if (queryParamId) return queryParamId

		return parsed.pathname.split('/').filter(Boolean).at(-1) || ''
	} catch {
		return normalizedUrl.split('/').filter(Boolean).at(-1) || ''
	}
}

const youtubeVideoId = computed(() => {
	if (!isYouTubeBlock.value) return ''
	return extractVideoID(props.url || props.value?.url)
})

const formatTime = (seconds) => {
	if (isNaN(seconds)) return '00:00'
	const mins = Math.floor(seconds / 60)
	const secs = Math.floor(seconds % 60)
	return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
}

const cursorTime = computed(() => formatTime(displayTime.value))
const totalTime = computed(() => formatTime(duration.value))
const cursorPosition = computed(() => {
	if (duration.value === 0) return 0
	return (displayTime.value / duration.value) * 100
})

const getVideoEl = () => videoPlayer.value?.querySelector('video')
const getYouTubePlayer = () => {
	const maybePlayer = youtubePlayerRef.value?.player
	if (!maybePlayer) return null
	if (typeof maybePlayer.playVideo === 'function') return maybePlayer
	if (typeof maybePlayer.value?.playVideo === 'function') return maybePlayer.value
	return null
}

const fadeOutCover = (coverElement, onComplete) => {
	if (!coverElement) {
		onComplete?.()
		return
	}

	gsap.killTweensOf(coverElement)
	gsap.to(coverElement, {
		opacity: 0,
		duration: 0.33,
		ease: 'power3.inOut',
		onComplete,
	})
}

const hideCover = (coverElement) => {
	if (hasStarted.value || isCoverFading.value) return

	isCoverFading.value = true
	fadeOutCover(coverElement, () => {
		hasStarted.value = true
		isCoverFading.value = false
	})
}

const handleCdnCoverClick = () => {
	const video = getVideoEl()
	if (!video) return

	video.play()
}

const handleYouTubeCoverClick = () => {
	const player = getYouTubePlayer()
	if (!player) return

	player.playVideo()
}

const handlePlayStop = () => {
	const video = getVideoEl()
	if (!video) return

	if (isPlaying.value) video.pause()
	else video.play()
}

const handleMute = () => {
	const video = getVideoEl()
	if (!video) return

	video.muted = !isMuted.value
	isMuted.value = video.muted
}

const handleMaximize = () => {
	const video = getVideoEl()
	if (!video) return

	if (video.requestFullscreen) video.requestFullscreen()
	else if (video.webkitRequestFullscreen) video.webkitRequestFullscreen()
	else if (video.msRequestFullscreen) video.msRequestFullscreen()
}

const handleMouseEnter = () => {
	showControls.value = true
	if (controlsRef.value) {
		gsap.killTweensOf(controlsRef.value)
		gsap.to(controlsRef.value, { opacity: 1, duration: 0.25, ease: 'power2.out' })
	}
}

const handleMouseLeave = () => {
	if (!isPlaying.value) return

	showControls.value = false
	if (controlsRef.value) {
		gsap.killTweensOf(controlsRef.value)
		if (gsap.getProperty(controlsRef.value, 'opacity') == 1) gsap.to(controlsRef.value, { opacity: 0, duration: 0.25, delay: 2, ease: 'power2.out' })
		else gsap.to(controlsRef.value, { opacity: 0, duration: 0.25, ease: 'power2.out' })
	}
}

const handleTimeBarClick = (e) => {
	if (!timeBar.value || !duration.value) return

	const video = getVideoEl()
	if (!video) return

	const rect = timeBar.value.getBoundingClientRect()
	const clickPosition = (e.clientX - rect.left) / rect.width
	const newTime = Math.max(0, Math.min(clickPosition * duration.value, duration.value))

	currentTime.value = newTime
	displayTime.value = newTime
	gsap.killTweensOf(displayTime)
	video.currentTime = newTime
}

const updateTime = (e) => {
	currentTime.value = e.target.currentTime

	if (e.target.currentTime < 0.1) {
		gsap.killTweensOf(displayTime)
		displayTime.value = 0
	} else {
		gsap.to(displayTime, {
			value: currentTime.value,
			duration: 0.5,
			ease: 'power2.out',
		})
	}
}

const updatePlayIcon = (e) => {
	if (e.type == 'pause') {
		isPlaying.value = false
		showControls.value = true
	} else {
		isPlaying.value = true
		hideCover(cdnCoverRef.value)
	}
}

const updateDuration = (e) => {
	duration.value = e.target.duration
}

const setup = () => {
	isTouch.value = screen.touch
	if (!isCdnVideoBlock.value) return

	const video = getVideoEl()
	if (!video) return

	video.addEventListener('timeupdate', updateTime)
	video.addEventListener('loadedmetadata', updateDuration)
	video.addEventListener('play', updatePlayIcon)
	video.addEventListener('pause', updatePlayIcon)
	isMuted.value = video.muted

	if (video.duration) duration.value = video.duration
}

const kill = () => {
	const video = getVideoEl()
	if (video) {
		video.removeEventListener('timeupdate', updateTime)
		video.removeEventListener('loadedmetadata', updateDuration)
		video.removeEventListener('play', updatePlayIcon)
		video.removeEventListener('pause', updatePlayIcon)
	}

	gsap.killTweensOf(displayTime)
	if (controlsRef.value) gsap.killTweensOf(controlsRef.value)
	if (cdnCoverRef.value) gsap.killTweensOf(cdnCoverRef.value)
	if (youtubeCoverRef.value) gsap.killTweensOf(youtubeCoverRef.value)
}

function stateChange(event) {
	isPlaying.value = event.data === 1
	if (event.data === 1) hideCover(youtubeCoverRef.value)
}

onBeforeUnmount(() => {
	kill()
})

onMounted(() => {
	context?.$page?.loader?.ready?.then(() => setup()) || setup()
})
</script>

<style lang="scss" scoped>
[data-component='body-video'] {
	position: relative;
	user-select: none;

	padding-right: column-width(1, true);

	.youtube-container {
		position: relative;

		border-radius: rs(12, 12);
		overflow: hidden;

		:deep(iframe) {
			width: 100%;
			height: 100%;
		}
	}

	.video-container {
		position: relative;
		border-radius: rs(12, 12);
		overflow: hidden;
	}

	.video-cover {
		position: absolute;
		inset: 0;
		z-index: 20;
		padding: rs(24, 12);

		display: flex;
		flex-direction: column;
		justify-content: flex-end;
		align-items: flex-start;
		gap: rs(4, 4);

		cursor: pointer;

		border: 0;
		margin: 0;
		width: 100%;
		background: none;

		.video-grad {
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			z-index: -1;
			background: linear-gradient(180deg, rgba(0, 0, 0, 0) 19.34%, rgba(0, 0, 0, 0.7) 98.2%);
		}

		.video-title {
			color: var(--color-geral-white);
			text-align: left;
			margin: 0;
		}

		.video-play {
			position: absolute;
			width: rs(100, 50);
			height: rs(100, 50);
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%);

			background-color: var(--color-river-blue-500);

			border-radius: 100%;

			display: flex;
			justify-content: center;
			align-items: center;

			--icon-color: var(--color-geral-white);

			svg {
				width: 100%;
				height: 100%;
			}
		}

		.video-tag {
			display: flex;
			flex-direction: row;
			justify-content: center;
			align-items: center;
			gap: rs(6, 6);

			--icon-color: var(--color-river-blue-200);

			p {
				margin: 0;
				color: var(--color-river-blue-200);
			}
		}
	}

	.controls-container {
		position: absolute;
		bottom: 0;
		width: 100%;
		--icon-color: var(--color-river-blue-500);

		display: flex;
		z-index: 10;
		justify-content: center;

		.-label-regular {
			color: var(--color-river-blue-500);
		}

		.play-icon {
			height: rs(42, 42);
			width: rs(42, 42);
			cursor: pointer;
		}

		.mute-icon {
			height: rs(28, 28);
			width: rs(28, 28);
			cursor: pointer;
			--icon-color: var(--color-river-blue-500);
			opacity: 0.5;

			&.active {
				--icon-color: var(--color-river-blue-500);
				opacity: 1;
			}
		}

		.maximize-icon {
			height: rs(24, 24);
			width: rs(24, 24);
			cursor: pointer;
		}
	}

	.controls {
		display: flex;
		flex-direction: column;
		gap: rs(6, 6);
		margin-bottom: rs(50, 50);
		padding-left: rs(30, 30);
		padding-right: rs(30, 30);
		padding-top: rs(12, 12);
		padding-bottom: rs(15, 15);
		border-radius: rs(36, 36);
		width: fit-content;
		background-color: rgb(from var(--color-river-blue-50) r g b / 0.5);
		backdrop-filter: blur(12px);

		.row {
			width: 100%;
			display: flex;
			flex-direction: row;
			justify-content: space-between;
			align-items: center;
			gap: rs(16, 16);
		}

		.time-bar-container {
			display: flex;
			flex-direction: row;
			position: relative;
			align-items: center;
			width: rs(250);
			padding: rs(10, 10);
			cursor: pointer;

			.current-time-bar,
			.time-bar {
				position: absolute;
				left: 0;
				height: rs(5, 5);
				border-radius: rs(5, 5);
				width: 100%;
			}

			.time-bar {
				background-color: var(--color-geral-white);
			}

			.current-time-bar {
				background-color: var(--color-river-blue-500);
			}

			.cursor {
				position: absolute;
				width: rs(20, 20);
				height: rs(15, 15);
				border-radius: rs(15, 15);
				box-shadow: -3px 3px 4px 0 rgba(0, 0, 0, 0.25);
				background-color: var(--color-river-blue-500);
				transform: translateX(-50%);
			}
		}
	}
}
</style>
